<template>
  <div class="text-gray-600">
    <div class="flex justify-center">
      <div
        ref="scrollContainer"
        class="reviews-container overflow-x-scroll disable-scrollbars flex flex-col pl-5 pt-3 md:pt-4 pb-2 md:pl-9 lg:pl-0"
        tabindex="0"
        role="region"
        aria-label="Customer reviews"
        @keydown.left="scrollLeft"
        @keydown.right="scrollRight"
      >
        <p class="ota-p16 font-extrabold md:ota-subtitle mb-2 pl-5 md:pl-9 lg:pl-0" v-text="$t(title)" />
        <div class="flex">
          <div v-for="(review, index) in reviews" :key="index" class="review-block flex flex-col">
            <div class="flex justify-between">
              <p class="ota-body" v-text="review.name" />
              <span class="ota-small-light italic" v-text="review.city" />
            </div>
            <StarRating
              :key="`${index}-review-key`"
              class="mb-4 inline-block mr-auto"
              :rating="review.rating"
              hide-title
              hide-icon
              hide-statistics
            />
            <p class="ota-small-light flex-1" v-text="review.review" />
            <i18n v-if="review.link" tag="p" class="o-footer mt-5 inline-block text-left p-2 rounded bg-gray-100" path="HomePage.LocizeReviews.ViewReview">
              <template #here>
                <a
                  class="underline"
                  :href="review.link"
                  :aria-label="$t('Accessibilty.AriaLabels.GoToGoogleReview')"
                  target="_blank"
                  rel="noopener noreferrer"
                  v-text="$t('HomePage.LocizeReviews.Here')"
                />
              </template>
            </i18n>
          </div>
          <div class="pr-1 md:pr-0" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { capitalize, getRandomIndexes } from '@/utils/general'
import { WebsiteCategories } from '@/enums/app'
const StarRating = () => import('@/components/StarRating')

export default {
  name: 'Reviews',
  components: {
    StarRating
  },
  props: {
    title: {
      type: String,
      default: 'HomePage.Reviews'
    },
    type: {
      type: String,
      default: WebsiteCategories.men,
      validator: value => [WebsiteCategories.men, WebsiteCategories.women].includes(value)
    }
  },
  computed: {
    reviews () {
      const path = `HomePage.LocizeReviews.${capitalize(this.type)}`
      const items = this.$t(path)

      if (!Array.isArray(items)) {
        return []
      }

      return getRandomIndexes(items, 3).map(i => ({
        city: this.$t(`${path}[${i}].City`, this.variables),
        link: (this.$t(`${path}[${i}].Link`, this.variables) ?? '').includes(`HomePage.LocizeReviews.${capitalize(this.type)}`) ? '' : this.$t(`${path}[${i}].Link`, this.variables),
        name: this.$t(`${path}[${i}].Name`, this.variables),
        rating: this.$t(`${path}[${i}].Rating`, this.variables),
        review: this.$t(`${path}[${i}].Review`, this.variables)
      }))
    },
    variables () {
      return {}
    }
  },
  methods: {
    scrollLeft () {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollLeft -= 200
      }
    },
    scrollRight () {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollLeft += 200
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
.reviews-container {
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;

  &:focus {
    outline: 2px solid #36C0B6;
    outline-offset: -2px;
  }
}

.disable-scrollbars {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Webkit */
  }
}

.review-block {
    @apply bg-white py-7 px-4 mr-5 shadow-md;
    scroll-snap-align: center;
    border-radius: 1rem;
    min-width: 25rem;
    max-width: 25rem;
}

@screen xsplus {
  .review-block {
    min-width: 35.334rem;
    max-width: 35.334rem;
  }
}

.star-rating >>> .star-rating-stars,
.star-rating >>> {
  > div {
    @apply justify-start mt-0;
  }
}

@screen md {
  .review-block {
    @apply flex-1;

    &:nth-child(3) {
      @apply mr-0;
    }
  }

  .star-rating {
    @apply pt-4;
  }
}
</style>
